import { useState } from 'react';
import { 
  FaSearch, 
  FaFilter, 
  FaChalkboardTeacher,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendar,
  FaClock,
  FaEnvelope
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import { formatDateLocale } from '../../utils/dateFormatter';

function AllClasses() {
  const [trainerFilter, setTrainerFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (trainerFilter) params.append('trainerId', trainerFilter);
    if (isActiveFilter !== '') params.append('isActive', isActiveFilter);
    params.append('page', page.toString());
    params.append('limit', '12');
    return `/api/admin/classes?${params.toString()}`;
  };

  const { data, error } = useSWR(buildQuery(), Fetcher);
  const classes = data?.data?.classes || [];
  const pagination = data?.data?.pagination || { total: 0, pages: 1 };

  const filteredClasses = classes.filter(classItem => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        classItem.classType?.toLowerCase().includes(query) ||
        classItem.day?.toLowerCase().includes(query) ||
        (classItem.programId && typeof classItem.programId === 'object' && classItem.programId.name?.toLowerCase().includes(query)) ||
        (classItem.trainerId && typeof classItem.trainerId === 'object' && classItem.trainerId.name?.toLowerCase().includes(query))
      );
    }
    return true;
  });

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Classes</h1>
          <p className="text-gray-600">View and manage all scheduled classes</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search classes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <select
              value={isActiveFilter}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button
              onClick={() => {
                setTrainerFilter('');
                setIsActiveFilter('');
                setSearchQuery('');
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Classes Grid */}
        {error ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-red-600">Failed to load classes</p>
          </div>
        ) : filteredClasses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaChalkboardTeacher className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Classes Found</h2>
            <p className="text-gray-600">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {filteredClasses.map((classItem) => (
                <div key={classItem._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{classItem.classType}</h3>
                      {classItem.programId && typeof classItem.programId === 'object' && (
                        <p className="text-sm text-gray-600 mb-2">{classItem.programId.name}</p>
                      )}
                      {classItem.trainerId && typeof classItem.trainerId === 'object' && (
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <FaUser className="mr-2 text-gray-400" />
                            <span className="capitalize">{classItem.trainerId.name}</span>
                            {classItem.trainerId.service && (
                              <span className="ml-2 text-gray-500">({classItem.trainerId.service})</span>
                            )}
                          </div>
                          {classItem.trainerId.email && (
                            <div className="flex items-center text-xs text-gray-500 ml-6">
                              <FaEnvelope className="mr-1" />
                              <span className="truncate">{classItem.trainerId.email}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        classItem.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {classItem.isActive ? (
                        <FaCheckCircle className="inline mr-1" />
                      ) : (
                        <FaTimesCircle className="inline mr-1" />
                      )}
                      {classItem.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaCalendar className="mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Day:</span>
                      <span>{classItem.day}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaClock className="mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Time:</span>
                      <span>{classItem.time}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium text-gray-900">{classItem.classDuration}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Max Students:</span>
                      <span className="font-medium text-gray-900">{classItem.maxStudents}</span>
                    </div>
                    {classItem.repeat && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Repeats:</span> {classItem.repeatDays?.join(', ') || 'N/A'}
                        {classItem.repeatUntil && (
                          <span className="ml-2">until {formatDateLocale(classItem.repeatUntil)}</span>
                        )}
                      </div>
                    )}
                    {classItem.enrolledCount !== undefined ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Enrolled:</span>
                        <span className="font-medium text-gray-900">
                          {classItem.enrolledCount} / {classItem.maxStudents}
                        </span>
                      </div>
                    ) : classItem.enrolledStudents && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Enrolled:</span>
                        <span className="font-medium text-gray-900">
                          {classItem.enrolledStudents.length} / {classItem.maxStudents}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AllClasses;

